import type {NexusHandler} from "@otto-ec/global-resources/nexus";
import {type ThirdpartyTracking, thirdpartyTracking, type TrackFirebaseResult,} from "./index";
import {AppType, appType, supportedAppVersion} from "../helper/appInfo";
import type {AdjustPayload} from "./adjustPayload";
import type {FirebasePayload} from "./firebasePayload";
import {getAdjustTrackingConsent} from "@otto-ec/eprivacy-cmp";
import {firebasePayloadFromObject} from "./types/firebasePayloadValidation";

const MIN_ANDROID_VERSION = "13.47.1";
const MIN_IOS_VERSION = "0.00.0";

export const trackAdjust: NexusHandler<ThirdpartyTracking["trackAdjust"]> = async function (adjustPayload: AdjustPayload) {
    if (!await getAdjustTrackingConsent()) {
        return;
    }
    if (isAndroidApp()) {
        const androidAdjustPayload = JSON.stringify(adjustPayload);
        window.o_apps_android?.trackAdjust?.(androidAdjustPayload);
    } else if (isIOSApp()) {
        window.webkit?.messageHandlers?.trackAdjust?.postMessage(adjustPayload);
    }
};

export const trackFirebase: NexusHandler<ThirdpartyTracking["trackFirebase"]> = async function (
    firebasePayload: FirebasePayload,
): Promise<TrackFirebaseResult> {
    const parsedPayload = await firebasePayloadFromObject(firebasePayload);
    if ("validation_error" in parsedPayload) {
        return parsedPayload;
    }
    const validPayload = parsedPayload.data;
    const isSingleItemPayload = !("items" in validPayload.event_params);

    if (isAndroidApp() && !isSupportedAppVersion() && !isSingleItemPayload) {
        return androidCompatibilityError();
    }

    if (isAndroidApp() && (isSupportedAppVersion() || isSingleItemPayload)) {
        const androidFirebasePayload = JSON.stringify(validPayload);
        window.o_apps_android?.trackFirebase?.(androidFirebasePayload);
    }

    if (isIOSApp()) {
        window.webkit?.messageHandlers?.trackFirebase?.postMessage(validPayload);
    }

    return { success: true };
};

function isSupportedAppVersion(): boolean {
    return supportedAppVersion({
        android: MIN_ANDROID_VERSION,
        ios: MIN_IOS_VERSION,
    });
}

function androidCompatibilityError(): TrackFirebaseResult {
    return {
        validation_error: {
            compatibility_error: "Android App versions older than 13.47.1 do not support items array payload."
        }
    };
}

function isAndroidApp(): boolean {
    return appType() === AppType.ANDROID;
}

function isIOSApp(): boolean {
    return appType() === AppType.IOS;
}

thirdpartyTracking.assignNamespace({trackAdjust, trackFirebase});
